import { format } from "date-fns";
import { useState } from "react";
import { Link } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import { useAPI } from "../contexts/APIContext";
import {
  AppointmentStatus,
  formatDuration,
  formatTimeSlot,
  generatePageNumbers,
  getAppointmentStatus,
  groupByDate,
} from "../utils/appointment";
import usePagination from "../utils/usePagination";

type Appointment = User.Schedule.QueryAppointmentsReturn["slots"][0];

export default function AppointmentsPage() {
  const { api, user } = useAPI();
  const [startDate, setStartDate] = useState<Date>(new Date(new Date().getTime() - 1000 * 60 * 60 * 2));
  const itemsPerPage = 10;

  // Use the pagination hook to load appointments
  const appointmentsPagination = usePagination<Appointment>({
    fn: async ({ offset, limit }) => {
      const result = await api.user.schedule.queryAppointments({
        start: startDate,
        offset,
        limit: limit || itemsPerPage,
      });

      return { count: result.count, items: result.slots };
    },
    initial: true,
    itemsPerPage,
  });

  // Group appointments by date using our utility function
  const groupedAppointments = groupByDate(appointmentsPagination.data);

  // Handle appointment cancellation
  const handleCancelAppointment = async ({ scheduleSlotId, userId }: Appointment) => {
    try {
      // Confirm cancellation
      if (!window.confirm("Are you sure you want to cancel this appointment?")) {
        return;
      }

      // Use the existing API method to cancel a schedule slot
      await api.user.schedule.cancelBooking({ scheduleSlotId, userId });

      // Refresh the list after cancellation
      appointmentsPagination.refresh();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      alert("Failed to cancel the appointment. Please try again.");
    }
  };

  // Handle date filter change
  const handleDateFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value;
    if (dateValue) {
      setStartDate(new Date(dateValue));
      appointmentsPagination.refresh();
    }
  };

  // Pagination controls
  const totalPages = Math.ceil(appointmentsPagination.count / itemsPerPage);
  const pageNumbers = generatePageNumbers(appointmentsPagination.currentPage, totalPages);

  // Handle page change
  const handlePageChange = (page: number) => {
    appointmentsPagination.loadPage(page);
  };

  // Check if an appointment was created by the current user
  const isAppointmentCreatedByUser = (appointment: Appointment) => {
    return appointment.user.userId === user?.userId;
  };

  // Get status label with appropriate color
  const getStatusLabel = (status: AppointmentStatus) => {
    switch (status) {
      case "in-session":
        return <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">In Session</span>;
      case "completed":
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Completed</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 w-full max-w-screen-md mx-auto sm:px-4 sm:py-8">
        <div className="bg-white overflow-hidden mb-8">
          <div className="p-4 flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <label htmlFor="dateFilter" className="text-sm text-gray-600 whitespace-nowrap">
                From:
              </label>
              <input
                id="dateFilter"
                type="date"
                value={format(startDate, "yyyy-MM-dd")}
                onChange={handleDateFilterChange}
                className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 flex-1 sm:flex-none"
              />
            </div>
          </div>

          {appointmentsPagination.isLoading && appointmentsPagination.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-pulse flex flex-col items-center">
                <svg
                  className="w-12 h-12 mb-3 text-blue-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
                <p className="text-gray-500">Loading appointments...</p>
              </div>
            </div>
          ) : appointmentsPagination.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <svg
                className="w-16 h-16 mb-6 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              <h3 className="mb-2 text-lg font-medium text-gray-800">No sessions found</h3>
              <p className="text-gray-500 mb-6 max-w-md">
                You don't have any upcoming language sessions scheduled for this period.
              </p>
              <Link
                to="/dashboard"
                className="px-4 py-2 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors"
              >
                Find Language Partners
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {groupedAppointments.map(([dateStr, appointments]) => (
                <div key={dateStr} className="p-4 sm:p-6">
                  <h3 className="font-medium text-lg text-gray-800 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3">
                      {format(new Date(dateStr), "d")}
                    </span>
                    {format(new Date(dateStr), "EEEE, MMMM d, yyyy")}
                  </h3>
                  <div className="space-y-3">
                    {appointments.map((appointment) => {
                      const isCreatedByUser = isAppointmentCreatedByUser(appointment);
                      const otherUser = isCreatedByUser ? appointment.bookedByUser : appointment.user;
                      const status = getAppointmentStatus(appointment.startsAt, appointment.duration);
                      const statusLabel = getStatusLabel(status);
                      const isPast = status === "completed";
                      const isInSession = status === "in-session";

                      return (
                        <div
                          key={appointment.scheduleSlotId}
                          className={`border rounded-xl p-4 transition-all ${
                            isInSession
                              ? "border-green-200 bg-green-50/30"
                              : isPast
                              ? "border-gray-200 bg-gray-50"
                              : "border-blue-100 hover:border-blue-300 hover:shadow-md"
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <div
                                className={`bg-white border border-gray-200 rounded-lg p-2 text-center min-w-16 hidden sm:block ${
                                  isInSession ? "border-green-300" : ""
                                }`}
                              >
                                <div className="text-xl font-semibold text-gray-800">
                                  {format(new Date(appointment.startsAt), "h:mm")}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {format(new Date(appointment.startsAt), "a")}
                                </div>
                              </div>

                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-800 sm:hidden">
                                    {formatTimeSlot(appointment.startsAt, appointment.duration)}
                                  </span>
                                  <span className="hidden sm:inline font-semibold text-gray-800">
                                    {formatDuration(appointment.duration)} session
                                  </span>
                                  {statusLabel}
                                </div>

                                <div className="mt-2 flex items-center">
                                  <img
                                    src={otherUser?.profileImageUrl || "https://via.placeholder.com/32"}
                                    alt={otherUser?.name}
                                    className="w-6 h-6 rounded-full mr-2"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {isCreatedByUser ? "Booked by " : "Session with "}
                                    <Link
                                      to={`/profile/${otherUser?.userId}`}
                                      className="font-medium text-blue-600 hover:underline"
                                    >
                                      {otherUser?.name}
                                    </Link>
                                  </span>
                                </div>

                                <div className="text-sm text-gray-500 mt-1 sm:hidden">
                                  {formatDuration(appointment.duration)} session
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                              {!isPast && !isInSession && (
                                <button
                                  onClick={() => handleCancelAppointment(appointment)}
                                  className="text-sm px-3 py-1.5 border border-red-200 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors"
                                >
                                  Cancel
                                </button>
                              )}
                              {isInSession && (
                                <Link
                                  to={`/chat/${otherUser?.userId}`}
                                  className="text-sm px-3 py-1.5 bg-green-50 text-green-600 rounded-full hover:bg-green-100 transition-colors"
                                >
                                  Join Session
                                </Link>
                              )}
                              <Link
                                to={`/profile/${otherUser?.userId}`}
                                className="text-sm px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                              >
                                View Profile
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {appointmentsPagination.isLoading && (
                <div className="flex justify-center py-4">
                  <div className="inline-flex items-center text-gray-500">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-blue-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading more appointments...
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(appointmentsPagination.currentPage - 1)}
                disabled={appointmentsPagination.currentPage === 1 || appointmentsPagination.isLoading}
                className={`relative inline-flex items-center px-3 py-2 rounded-l-md border ${
                  appointmentsPagination.currentPage === 1 || appointmentsPagination.isLoading
                    ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              {pageNumbers.map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  disabled={appointmentsPagination.isLoading}
                  className={`relative inline-flex items-center px-4 py-2 border ${
                    pageNum === appointmentsPagination.currentPage
                      ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(appointmentsPagination.currentPage + 1)}
                disabled={appointmentsPagination.currentPage === totalPages || appointmentsPagination.isLoading}
                className={`relative inline-flex items-center px-3 py-2 rounded-r-md border ${
                  appointmentsPagination.currentPage === totalPages || appointmentsPagination.isLoading
                    ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </nav>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
